import { toWebpImageUrl } from '../../utility/image-utility';
import { Component, ElementRef, HostBinding, Inject, Input, OnDestroy, OnInit, PLATFORM_ID, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CarouselComponent, SlidesOutputData } from 'ngx-owl-carousel-o';
import { OwlCarouselOConfig } from 'ngx-owl-carousel-o/lib/carousel/owl-carousel-o-config';
import { PhotoSwipeItem, PhotoSwipeModelRef, PhotoSwipeService, PhotoSwipeThumbBounds } from '../../services/photo-swipe.service';
import { DirectionService } from '../../services/direction.service';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { ProductLayout } from '../product/product.component';
import { NavigationStart, Router } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export interface ProductGalleryItem {
    id: string;
    image: string;
    video?:string;
}

@Component({
    selector: 'app-product-gallery',
    templateUrl: './product-gallery.component.html',
    styleUrls: ['./product-gallery.component.scss']
})
export class ProductGalleryComponent implements OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();

    items: ProductGalleryItem[] = []; 
    currentItem: ProductGalleryItem = null;

    carouselOptions: Partial<OwlCarouselOConfig> = {
        dots: false,
        autoplay: false,
        responsive: {
            0: {items: 1}
        },
        rtl: this.direction.isRTL()
    };

    thumbnailsCarouselOptions: Partial<OwlCarouselOConfig> = {
        dots: false,
        autoplay: false,
        rewind:true,
        autoplayHoverPause:true,
        autoplayTimeout:9000,
        margin: 8,
        items: 7,
        responsive: {
            480: {items: 7},
            380: {items: 6},
            0: {items: 5}
        },
        rtl: this.direction.isRTL()
    };

    galleryRef: PhotoSwipeModelRef = null;

    galleryIsClosed = false;
    zoomBehave:string = 'hover';

    @ViewChild('videoPlayer') video: ElementRef<HTMLVideoElement>;
    showPlayButton = true;

    play(): void 
    { 
        const video: HTMLVideoElement = this.video.nativeElement;
        video.play();
        this.showPlayButton = false;
    }

    onPlay(): void {
        this.showPlayButton = false;
    }

    onPause(): void {
        this.showPlayButton = true;
    }

    @Input() productLayout: ProductLayout;

    @Input() productName: string; 

    @Input() videoPoster: string;

    @Input() set images(images: string[]) {
        this.items = images.map((image, index) => { 
            if (this.isVideoUrl(image)) { 
                this.zoomBehave = 'none' 
                return { id: 'vid', image: this.videoPoster, video: image};
              } else { 
                return { id: `image-${index}`, image };
              }
          });
        this.currentItem = this.items[0] || null; 
    }  

    @HostBinding('class.product-gallery') classProductGallery = true;

    @ViewChild('featuredCarousel', { read: CarouselComponent }) featuredCarousel: CarouselComponent;

    @ViewChild('thumbnailsCarousel', { read: CarouselComponent }) thumbnailsCarousel: CarouselComponent;

    @ViewChildren('imageElement', {read: ElementRef}) imageElements: QueryList<ElementRef>;
    isServer:boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private photoSwipe: PhotoSwipeService,
        private direction: DirectionService,
        private router: Router, 
    ) { this.isServer = isPlatformServer(this.platformId);}

    ngOnInit(): void {

        // this.items.push({
        //     id: 'vid',
        //     image:this.items[0].image,
        //     video:'https://localhost:44314/filesystem/videos/test-video.mp4'
        //   });
         
        if (this.productLayout !== 'quickview' && isPlatformBrowser(this.platformId)) {
            this.photoSwipe.load().subscribe();
        }

        this.router.events.pipe(
            filter(event => event instanceof NavigationStart),
            takeUntil(this.destroy$),
        ).subscribe(() => {
            this.galleryIsClosed = true;

            if (this.galleryRef) {
                this.galleryRef.close();
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    featuredCarouselTranslated(event: SlidesOutputData): void {
        if (event.slides.length) {
            const activeImageId = event.slides[0].id; 
            this.currentItem = this.items.find(x => x.id === activeImageId) || this.items[0] || null;

            if (!this.thumbnailsCarousel.slidesData.find(slide => slide.id === activeImageId && slide.isActive)) 
            {
                this.thumbnailsCarousel.to(activeImageId);
                if(this.currentItem.id == "vid"){
                    this.zoomBehave = 'none'
                }
                else{
                    this.zoomBehave = 'hover';
                }
            } 
        }
    }

    getDirDependentIndex(index) {
        // we need to invert index id direction === 'rtl' because photoswipe do not support rtl
        if (this.direction.isRTL()) {
            return this.items.length - 1 - index;
        }

        return index;
    }

    onFeaturedImageClick(event: MouseEvent, image: any): void {
        if (this.productLayout !== 'quickview') {
            event.preventDefault(); 
            this.openPhotoSwipe(image);
        }
    }

    onThumbnailImageClick(item: ProductGalleryItem): void { 
        this.featuredCarousel.to(item.id);
        this.currentItem = item;
        if(this.currentItem.id == "vid"){
            this.zoomBehave = 'none';
            if(this.video){
                this.video.nativeElement.play();
            } 
        }
        else{
            this.zoomBehave = 'hover';
            if(this.video){
                this.video.nativeElement.pause();
            } 
        } 
    }

    openPhotoSwipe(item: ProductGalleryItem): void {
        if (!item || item.id == 'vid') {
            return;
        }

        const imageElements = this.imageElements.map(x => x.nativeElement);
        const images: PhotoSwipeItem[] = this.items.map((eachItem, i) => {
            const tag: HTMLImageElement = imageElements[i];
            const width = parseFloat(tag.dataset.width) || tag.naturalWidth;
            const height = parseFloat(tag.dataset.height) || tag.naturalHeight;

            return {
                src: eachItem.image,
                msrc: eachItem.image,
                w: width,
                h: height,
            };
        });

        if (this.direction.isRTL()) {
            images.reverse();
        }

        // noinspection JSUnusedGlobalSymbols
        const options = {
            getThumbBoundsFn: index => {
                if (this.galleryIsClosed) {
                    return null;
                }

                return this.getThumbBounds(index);
            },
            index: this.getDirDependentIndex(this.items.indexOf(item)),
            bgOpacity: .9,
            history: false,
        };

        this.photoSwipe.open(images, options).subscribe(galleryRef => {
            galleryRef.listen('beforeChange', () => {
                this.featuredCarousel.to(this.items[this.getDirDependentIndex(galleryRef.getCurrentIndex())].id);
            });

            this.galleryRef = galleryRef;
        });
    }

    getThumbBounds(index: number): PhotoSwipeThumbBounds {
        const imageElements = this.imageElements.toArray();
        const dirDependentIndex = this.getDirDependentIndex(index);

        if (!imageElements[dirDependentIndex] || !isPlatformBrowser(this.platformId) || this.currentItem.id == "vid") {
            return null;
        }

        if (isPlatformBrowser(this.platformId)){
            const tag = imageElements[dirDependentIndex].nativeElement;
            const width = tag.naturalWidth;
            const height = tag.naturalHeight;
            const rect = tag.getBoundingClientRect();
            const ration = Math.min(rect.width / width, rect.height / height);
            const fitWidth = width * ration;
            const fitHeight = height * ration;
    
            return {
                x: rect.left + (rect.width - fitWidth) / 2 + window.pageXOffset,
                y: rect.top + (rect.height - fitHeight) / 2 + window.pageYOffset,
                w: fitWidth,
            };
        } 
    }

    /// Delegates to the shared helper so both product screens agree on which urls have a
    /// webp sibling. See toWebpImageUrl: remote (imported) images must be left alone.
    getWebpImageUrl(imageUrl: string): string {
        return toWebpImageUrl(imageUrl);
    }

    /// By the path's extension, query and fragment left off. Not `new URL(url)`: it throws on a relative
    /// address - which is what this site's own uploads are (/filesystem/...) - and that emptied the gallery.
    isVideoUrl(url: string): boolean {
        const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.flv', '.wmv'];
        const pathname = (url || '').split(/[?#]/)[0].toLowerCase();
        return videoExtensions.some(ext => pathname.endsWith(ext));
    }
}
